import { useAuth, useLogout, useSubscribeLinks } from '@/hooks';
import { SearchOverlay } from '@/components/search-overlay';
import { openConseillerHome, openConseillerSearch } from '@/lib/conseiller-search';
import { getShortcutGroups, isShortcutAvailable, shortcutKeys } from '@/lib/shortcuts';
import { hasAnyRole, hasPermission, hasRole } from '@/lib/permissions';
import { KeyboardShortcutsMenu } from '@workspace/backoffice/components/keyboard-shortcuts-menu';
import { useKeyboardShortcuts } from '@workspace/backoffice/hooks/use-keyboard-shortcuts';
import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar';
import { Button } from '@workspace/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { Separator } from '@workspace/ui/components/separator';
import { Input } from '@workspace/ui/components/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@workspace/ui/components/sheet';
import { Switch } from '@workspace/ui/components/switch';
import { useTheme } from '@workspace/ui/components/theme-provider';
import { BarChart3, CheckSquare, ExternalLink, FilePlus, FileText, Gauge, LogOut, Menu, Moon, Phone, Search, Sun, Users } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

interface AppNavbarProps {
    userName: string;
    userRoles: string[];
}

export function AppNavbar({ userName, userRoles }: AppNavbarProps) {
    const canSeeDashboard = hasAnyRole({ roles: userRoles } as Parameters<typeof hasAnyRole>[0], ['admin', 'gestionnaire televente', 'manager televente']);
    const { setTheme, theme } = useTheme();
    const { data: authData } = useAuth();
    const user = authData?.user;
    const canAccessTasks = hasPermission(user, 'read tasks deals');
    const logoutMutation = useLogout();
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Les liens de tarification télévente n'existent pas pour les gestionnaires commerciaux (mode courtier)
    const hasTeleventeLinks = hasRole(user, 'admin') || !hasRole(user, 'gestionnaire commercial');
    const { data: subscribeLinksData } = useSubscribeLinks({ enabled: hasTeleventeLinks });
    const nouveauDevisUrl = subscribeLinksData?.data?.april_moto?.autres;

    // Mêmes rôles que la route /dashboard/televente
    const canSeePilotage = hasAnyRole(user, ['admin', 'gestionnaire televente', 'manager televente', 'animateur televente']);
    const shortcutAccess = { canSeePilotage, hasTeleventeLinks: Boolean(nouveauDevisUrl) };

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Raccourcis clavier disponibles sur toutes les pages (lexique : touche ?)
    useKeyboardShortcuts([
        {
            key: shortcutKeys.nouveauDevis,
            handler: () => {
                if (nouveauDevisUrl) {
                    window.open(nouveauDevisUrl, '_blank', 'noopener,noreferrer');
                    return;
                }
                navigate('/nouveau-devis');
            },
        },
        { key: shortcutKeys.recherche, handler: () => setSearchOpen(true) },
        { key: shortcutKeys.appels, handler: () => navigate('/call-notifications') },
        { key: shortcutKeys.pilotage, handler: () => navigate('/dashboard/televente') },
        { key: shortcutKeys.conseiller, handler: openConseillerHome },
        { key: shortcutKeys.lexique, handler: () => setShortcutsOpen(true) },
    ].map((shortcut) => ({ ...shortcut, enabled: isShortcutAvailable(shortcut.key, shortcutAccess) })));

    const handleSearch = (event: FormEvent) => {
        event.preventDefault();
        if (!openConseillerSearch(search)) return;
        setSearch('');
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
        `relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:brightness-105'
        }`;

    const navItems = (
        <>
            <NavLink to="/quotes" className={navLinkClass}>
                <FileText className="size-4" />
                Devis
            </NavLink>
            <NavLink to="/nouveau-devis" className={navLinkClass}>
                <FilePlus className="size-4" />
                Nouveau devis
            </NavLink>
            <NavLink to="/contacts" className={navLinkClass}>
                <Users className="size-4" />
                Contacts
            </NavLink>
            {canAccessTasks && (
                <NavLink to="/tasks" className={navLinkClass}>
                    <CheckSquare className="size-4" />
                    Tâches
                </NavLink>
            )}
            <NavLink to="/call-notifications" className={navLinkClass}>
                <Phone className="size-4" />
                Appels
            </NavLink>
            {canSeeDashboard && (
                <NavLink to="/dashboard" end className={navLinkClass}>
                    <BarChart3 className="size-4" />
                    Dashboard
                </NavLink>
            )}
            {canSeeDashboard && (
                <NavLink to="/dashboard/televente" className={navLinkClass}>
                    <Gauge className="size-4" />
                    Pilotage
                </NavLink>
            )}
        </>
    );

    const themeToggle = (
        <div className="flex items-center gap-2">
            <Sun className="size-4 text-muted-foreground" />
            <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
            />
            <Moon className="size-4 text-muted-foreground" />
        </div>
    );

    return (
        <nav
            className={`sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-shadow duration-200 ${
                isScrolled ? 'shadow-md' : ''
            }`}
        >
            <div className="flex h-14 items-center justify-between px-4 lg:px-6">
                <div className="flex items-center gap-4 lg:gap-8">
                    {/* Mobile hamburger */}
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="lg:hidden">
                                <Menu className="size-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-64">
                            <SheetHeader>
                                <SheetTitle className="flex items-center gap-2">
                                    <img src="/april-moto.svg" alt="APRIL MOTO" className="size-6" />
                                    Deals
                                </SheetTitle>
                            </SheetHeader>
                            <div className="flex flex-col gap-1 px-2">
                                {navItems}
                            </div>
                            <div className="mt-auto px-4 pb-4">
                                <Separator className="mb-4" />
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Thème</span>
                                    {themeToggle}
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <NavLink to="/quotes" className="flex items-center gap-2 group">
                        <img
                            src="/april-moto.svg"
                            alt="APRIL MOTO"
                            className="size-7"
                        />
                        <span className="font-semibold text-lg group-hover:text-primary transition-colors">Deals</span>
                    </NavLink>

                    {/* Desktop nav */}
                    <div className="hidden lg:flex items-center gap-1">
                        {navItems}
                    </div>
                </div>

                <div className="flex items-center gap-2 lg:gap-3">
                    <form onSubmit={handleSearch} className="hidden lg:flex items-center relative">
                        <Search className="absolute left-2.5 size-4 text-muted-foreground pointer-events-none" />
                        <Input
                            type="search"
                            placeholder="Rechercher un client..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 w-52 h-9"
                        />
                        <ExternalLink className="absolute right-2.5 size-3.5 text-muted-foreground pointer-events-none" />
                    </form>

                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => setSearchOpen(true)}
                        aria-label="Rechercher un client"
                        title="Rechercher un client (R)"
                    >
                        <Search className="size-5" />
                    </Button>

                    <Separator orientation="vertical" className="hidden lg:block h-8" />

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="relative h-9 gap-2 rounded-full px-2 lg:px-3 lg:pl-2">
                                <Avatar className="h-8 w-8 border-2 border-primary/20 hover:border-primary transition-colors">
                                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold text-xs">
                                        {getInitials(userName)}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="hidden sm:inline text-sm font-medium">{userName}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="end" forceMount>
                            <DropdownMenuLabel className="font-normal text-right">
                                <div className="flex flex-col space-y-1 items-end">
                                    <p className="text-sm font-medium leading-none">{userName}</p>
                                    <p className="text-xs leading-none text-muted-foreground">Connecté</p>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => logoutMutation.mutate()}
                                disabled={logoutMutation.isPending}
                                className="text-red-600 focus:text-red-600 cursor-pointer justify-end"
                            >
                                <span>Déconnexion</span>
                                <LogOut className="ml-2 h-4 w-4" />
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Separator orientation="vertical" className="hidden lg:block h-8" />

                    <div className="hidden lg:block">
                        {themeToggle}
                    </div>

                    <KeyboardShortcutsMenu
                        open={shortcutsOpen}
                        onOpenChange={setShortcutsOpen}
                        groups={getShortcutGroups(shortcutAccess)}
                        className="hidden lg:flex"
                    />
                </div>
            </div>

            {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
        </nav>
    );
}
